import { Box, Slider, Typography } from '@mui/material';
import { CustomSwitch } from './StyledComponents';
import React from 'react';
import { Add, Remove, ZoomIn } from '@mui/icons-material';

const CustomSlider = ({
  value,
  onChange,
  min,
  max,
  step,
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
}) => {
  return (
    <input
      className="custom-slider"
      type="range"
      value={value}
      onChange={(e) => onChange(e.target.valueAsNumber)}
      min={min}
      max={max}
      step={step}
    />
  );
};

const ZoomSlider = ({
  zoomLevel,
  setZoomLevel,
}: {
  zoomLevel: number;
  setZoomLevel: (zoomLevel: number) => void;
}) => {
  const [minZoomLevel, maxZoomLevel] = [0.5, 1];

  return (
    <Box
      className="zoom-slider-container open"
      sx={{
        //position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        top: 10,
        right: 10,
        //width: '200px',
        width: '100%',
        height: '34px',
        padding: '5px',
        zIndex: 1,
        cursor: 'pointer',
      }}
    >
      <Typography>Zoom Level</Typography>
      <Box
        /*className={isOpen ? "inner-container vis" : "inner-container hid"}*/
        sx={{
          flexGrow: 1,
          transition: '0.5s ease-in-out',
          //marginRight: '35px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Remove
          sx={{ color: '#414753' }}
          onClick={() => setZoomLevel(Math.max(minZoomLevel, zoomLevel - 0.05))}
        />
        <CustomSlider
          min={minZoomLevel}
          step={0.05}
          max={maxZoomLevel}
          value={Math.max(minZoomLevel, Math.min(maxZoomLevel, zoomLevel))}
          onChange={(value) => setZoomLevel(value)}
        />
        <Add
          sx={{ color: '#414753' }}
          onClick={() => setZoomLevel(Math.min(maxZoomLevel, zoomLevel + 0.05))}
        />
      </Box>
      {process.env.REACT_APP_NODE_ENV != 'prd' && (
        <p
          style={{
            color: '#000',
            opacity: 0.5,
            position: 'absolute',
            right: 10,
            top: 30,
            fontSize: 12,
          }}
        >
          [debug] Zoom Level: {(zoomLevel ?? 0.9).toFixed(2)}
        </p>
      )}
    </Box>
  );
};

type SettingsPanelProps = {
  open: boolean;
  setZoomLevel: (zoomLevel: number) => void;
  setCursorVisible: (visible: boolean) => void;
  setPianoSound: (sound: boolean) => void;
  setMetronomeSound: (sound: boolean) => void;
  pianoSound: boolean;
  cursorVisible: boolean;
  zoomLevel: number;
  metronomeSound: boolean;
};

export default function SettingsPanel({
  zoomLevel,
  setZoomLevel,
  pianoSound,
  setPianoSound,
  metronomeSound,
  setMetronomeSound,
  cursorVisible,
  setCursorVisible,
}: SettingsPanelProps) {
  const rowStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  };
  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 100,
        left: '10px',
        bottom: '60px',
        backgroundColor: '#050B13',
        padding: '16px 32px',
        borderRadius: '8px',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10px', // Half of the carot size to move it above the modal
          left: '10px', // Align this with the hamburger icon
          width: '0',
          height: '0',
          borderLeft: '16px solid transparent',
          borderRight: '16px solid transparent',
          borderTop: '16px solid #050B13', // Carot color should match modal background
        }}
      />
      <Typography
        variant="h5"
        sx={{
          color: 'white',
          textTransform: 'uppercase',
          marginBottom: '16px',
          paddingBottom: '10px',
          borderBottom: '1px solid #414753',
        }}
      >
        Settings
      </Typography>
      <Box>
        <Box sx={{ ...rowStyles, flexDirection: 'column', marginBottom: '10px' }}>
          <ZoomSlider zoomLevel={zoomLevel} setZoomLevel={setZoomLevel} />
        </Box>
      </Box>
      <Box>
        <Box sx={rowStyles}>
          <CustomSwitch
            checked={pianoSound}
            onChange={(_, value) => setPianoSound(value as boolean)}
          />
          <Typography variant="body1">Piano Sound</Typography>
        </Box>
      </Box>
      <Box sx={rowStyles}>
        <CustomSwitch
          checked={metronomeSound}
          onChange={(_, value) => setMetronomeSound(value as boolean)}
        />
        <Typography variant="body1">Metronome Sound</Typography>
      </Box>
      <Box sx={rowStyles}>
        <CustomSwitch
          checked={cursorVisible}
          onChange={(_, value) => setCursorVisible(value as boolean)}
        />
        <Typography variant="body1">Cursor Visible</Typography>
      </Box>
    </Box>
  );
}
